const maxCronYear = 2199;
const minCronYear = 1970;
const numberRegExp = /^\d+$/;
const cronStandardRegExp =
  /^((\d+\/\d+)|(\d+-(\d+|\d+\/\d+))|(\d+)|(\*|\*\/\d+))$/;
const cronStandardRegExpChar =
  /^(([a-zA-Z]+\/\d+)|([a-zA-Z]+-([a-zA-Z]+|[a-zA-Z]+\/\d+))|[a-zA-Z]+)$/;
const cronDayOfMonthRegExp =
  /^((\d+\/\d+)|(\d+-(\d+|\d+\/\d+))|(\d+)|(\*|\*\/\d+)|(L(-\d+)?)|\?|\d+W|LW)$/;

const cronDayOfWeekRegExp =
  /^((\d+\/\d+)|(\d+-(\d+|\d+\/\d+))|\?|(\*|\*\/\d+)|\d+|\d?L|(L-\d+)|(\d+#\d+))$/;

const monthReplaces = [
  ['JAN', '1'],
  ['FEB', '2'],
  ['MAR', '3'],
  ['APR', '4'],
  ['MAY', '5'],
  ['JUN', '6'],
  ['JUL', '7'],
  ['AUG', '8'],
  ['SEP', '9'],
  ['OCT', '10'],
  ['NOV', '11'],
  ['DEC', '12'],
];

const dayWeekReplaces = [
  ['SUN', '1'],
  ['MON', '2'],
  ['TUE', '3'],
  ['WED', '4'],
  ['THU', '5'],
  ['FRI', '6'],
  ['SAT', '7'],
];

const endOfWeek = ['SUNL', 'MONL', 'TUEL', 'WEDL', 'THUL', 'FRIL', 'SATL'];
const lRegex = new RegExp('^[0-9]*L.*$');

const CRON_PART_TYPE = {
  minutes: 'minutes',
  hours: 'hours',
  dayOfMonth: 'dayOfMonth',
  month: 'month',
  dayOfWeek: 'dayOfWeek',
  year: 'year',
};

const getReplaceRegExp = (name) => new RegExp(`^${name}.*$`);

const isLastWeekDayofMonth = (str) => {
  // Check if t is the special cause of <WEEKDAY>L
  return endOfWeek.some((part) => getReplaceRegExp(part).test(str));
};

const regexReplace = (str, rules) => {
  let replacedString = str ? str.toUpperCase() : '';
  rules.forEach(([from, to]) => {
    replacedString = replacedString.replace(getReplaceRegExp(from), to);
  });
  return replacedString;
};

const filterBeforeReplace = (rules, replacements) => {
  if (
    rules.every((rule) =>
      replacements.some(([key]) => rule.toUpperCase().startsWith(key))
    )
  ) {
    if (!replacements.some(([key]) => rules[0].toUpperCase() === key)) {
      return [rules[0]];
    }
    const secondPart =
      replacements.find(([key]) =>
        rules[1].toUpperCase().startsWith(key)
      )?.[0] || rules[1];
    return [rules[0], secondPart];
  }
  return rules;
};

const verifyPartsAreEqual = (parts, replacementsRule) => {
  const values = replacementsRule.map(([key]) => key);
  const regexp = new RegExp(`^(${values.join('|')}){1}[A-Z]?$`);
  return (
    parts.every((part) => numberRegExp.test(part)) ||
    parts.every((part) => regexp.test(part.toUpperCase()))
  );
};

const verifyBeforeReplace = (str, replacementsRule) => {
  // Do not replace for <WeekDay>L
  if (lRegex.test(str) || isLastWeekDayofMonth(str)) {
    return true;
  }
  const splitRules = str.split(',');

  return splitRules.every((splitRule) => {
    const rule = splitRule.split('-');
    if (rule.length === 2) {
      return verifyPartsAreEqual(rule, replacementsRule);
    }
    if (rule.length === 1) {
      if (rule[0].indexOf('#') > -1) {
        const parts = rule[0].split('#');
        if (parts.length === 2) {
          return (
            verifyPartsAreEqual([parts[0]], replacementsRule) &&
            numberRegExp.test(parts[1])
          );
        }
        return false;
      }
      return verifyPartsAreEqual([rule[0]], replacementsRule);
    }
    return false;
  });
};

const replace = (str, replacementsRule) => {
  // Do not replace for <WeekDay>L
  if (isLastWeekDayofMonth(str)) {
    return str;
  }
  const splitRules = str.split(',');
  const returnArray = [];

  for (let i = 0; i < splitRules.length; i += 1) {
    const rule = splitRules[i].split('-');

    if (rule.length === 2) {
      returnArray.push(
        filterBeforeReplace(rule, replacementsRule)
          .map((dayOfWeek) => regexReplace(dayOfWeek, replacementsRule))
          .join('-')
      );
    } else if (rule.length === 1) {
      if (rule[0].indexOf('#') > -1) {
        const parts = rule[0].split('#');
        returnArray.push(
          `${regexReplace(parts[0], replacementsRule)}#${parts[1]}`
        );
      } else {
        returnArray.push(regexReplace(rule[0], replacementsRule));
      }
    } else {
      throw new Error(
        'replace - multiple "-" should have been caught by the validator'
      );
    }
  }

  return returnArray.join(',');
};

const isValidDate = (year, month, day) => {
  const thisDate = new Date(year, month - 1, day);
  return thisDate.getMonth() === month - 1;
};

const isWeekday = (year, month, day) => {
  if (day < 1 || day > 31) {
    return false;
  }
  const thisDate = new Date(year, month - 1, day);
  if (thisDate.getMonth() !== month - 1) {
    return false;
  }
  return thisDate.getDay() > 0 && thisDate.getDay() < 6;
};

const validLastDayOfWeekInMonth = (rule) => {
  // If Weekday has the value <WEEKDAY>L, it cannot contain another value
  return (
    rule.split(',').some((part) => isLastWeekDayofMonth(part)) &&
    rule.split(',').length > 1
  );
};

const validOnlySingleLRule = (rule) => {
  // There cannot be multiple L values - #L, L-3, L etc.
  return (
    rule.split(',').some((part) => lRegex.test(part)) &&
    rule.split(',').length > 1
  );
};

export const getDaysOfMonthFromDaysOfWeek = (year, month, daysOfWeek) => {
  const daysOfMonth = [];
  let index = 0; // only for "#" use case
  for (let i = 1; i <= 31; i += 1) {
    const thisDate = new Date(year, month - 1, i);
    // already after last day of month
    if (thisDate.getMonth() !== month - 1) {
      break;
    }
    if (daysOfWeek[0] === 'L') {
      if (daysOfWeek[1] === thisDate.getDay() + 1) {
        const sameDayNextWeek = new Date(thisDate.getTime() + 7 * 24 * 3600000);
        if (sameDayNextWeek.getMonth() !== thisDate.getMonth()) {
          return [i];
        }
      }
    } else if (daysOfWeek[0] === '#') {
      if (daysOfWeek[1] === thisDate.getDay() + 1) {
        index += 1;
      }
      if (daysOfWeek[2] === index) {
        return [i];
      }
    } else if (daysOfWeek.includes(thisDate.getDay() + 1)) {
      daysOfMonth.push(i);
    }
  }
  return daysOfMonth;
};

export const getDaysOfMonthForL = (year, month) => {
  for (let i = 31; i >= 28; i -= 1) {
    const thisDate = new Date(year, month - 1, i);
    if (thisDate.getMonth() === month - 1) {
      return [i];
    }
  }
  throw new Error('getDaysOfMonthForL - should not happen');
};

const getDaysOfMonthForW = (year, month, day) => {
  const offset = [0, 1, -1, 2, -2].find((c) => isWeekday(year, month, day + c));
  if (offset === undefined)
    throw new Error('getDaysOfMonthForW - should not happen');
  return [day + offset];
};

const getDaysOfMonthForLW = (year, month) => {
  for (let i = 31; i >= 26; i -= 1) {
    const thisDate = new Date(year, month - 1, i);
    if (thisDate.getMonth() === month - 1 && isWeekday(year, month, i)) {
      return [i];
    }
  }
  throw new Error('getDaysOfMonthForLW - should not happen');
};

const parseSingleRule = (rule, min, max) => {
  if (rule === '?') {
    return [];
  }
  if (rule === 'L') {
    return ['L'];
  }
  if (numberRegExp.test(rule)) {
    return [parseInt(rule, 10)];
  }
  if (isLastWeekDayofMonth(rule)) {
    return ['L', parseInt(replace(rule.substring(0, 3), dayWeekReplaces), 10)];
  }
  if (rule === 'LW') {
    return ['LW'];
  }
  if (rule.startsWith('L')) {
    return ['L', parseInt(rule.substring(1), 10)];
  }
  if (rule.endsWith('L')) {
    return ['L', parseInt(rule.substring(0, rule.length - 1), 10)];
  }
  if (rule.endsWith('W')) {
    return ['W', parseInt(rule.substring(0, rule.length - 1), 10)];
  }
  if (rule.includes('#')) {
    return [
      '#',
      parseInt(rule.split('#')[0], 10),
      parseInt(rule.split('#')[1], 10),
    ];
  }
  let allows = [];
  if (rule === '*') {
    let start = min;
    while (start <= max) {
      allows.push(start);
      start += 1;
    }
    return allows;
  }
  if (rule.includes(',')) {
    rule.split(',').forEach((part) => {
      allows = allows.concat(parseSingleRule(part, min, max));
    });
    return allows.sort((a, b) => a - b);
  }
  if (rule.includes('/')) {
    const ruleParts = rule.split('/');
    if (ruleParts[0] === '*') {
      ruleParts[0] = min.toString();
    } else if (ruleParts[0].includes('-')) {
      const innerParts = ruleParts[0].split('-');
      const newRule = `${innerParts[0]}/${ruleParts[1]}`;
      const start = parseInt(innerParts[0], 10);
      const end = parseInt(innerParts[1], 10);
      return parseSingleRule(newRule, start, end);
    }
    let start = parseInt(ruleParts[0], 10);
    let increment = parseInt(ruleParts[1], 10);

    if (rule.includes('*') && increment === 0) {
      increment = 1;
    }

    if (increment > 0) {
      while (start <= max) {
        allows.push(start);
        start += increment;
      }
    } else {
      allows.push(start);
    }
    return allows;
  }
  if (rule.includes('-')) {
    const parts = rule.split('-');
    const start = parseInt(parts[0], 10);
    const end = parseInt(parts[1], 10);
    if (end >= start) {
      for (let i = start; i <= end; i += 1) allows.push(i);
    } else {
      for (let i = min; i <= end; i += 1) allows.push(i);
      for (let i = start; i <= max; i += 1) allows.push(i);
    }
  }
  return allows;
};

// The Cron Validator runs after splitting along ',' and does not perform logic based evaluation
const regExpRulePartValidator = (rule, partType) => {
  switch (partType) {
    case CRON_PART_TYPE.minutes:
    case CRON_PART_TYPE.hours:
      return cronStandardRegExp.test(rule);
    case CRON_PART_TYPE.dayOfMonth:
      return cronDayOfMonthRegExp.test(rule);
    case CRON_PART_TYPE.month:
      return cronStandardRegExp.test(rule) || cronStandardRegExpChar.test(rule);
    case CRON_PART_TYPE.dayOfWeek:
      return (
        cronDayOfWeekRegExp.test(rule) ||
        (verifyBeforeReplace(rule, dayWeekReplaces) &&
          cronDayOfWeekRegExp.test(replace(rule, dayWeekReplaces))) ||
        cronStandardRegExpChar.test(rule)
      );
    case CRON_PART_TYPE.year:
      return cronStandardRegExp.test(rule);
    default:
      return false;
  }
};

function isValidCronRulePart(rule, partType) {
  if (
    partType === CRON_PART_TYPE.dayOfWeek &&
    validLastDayOfWeekInMonth(rule)
  ) {
    return false;
  }
  if (
    (partType === CRON_PART_TYPE.dayOfWeek ||
      partType === CRON_PART_TYPE.dayOfMonth) &&
    validOnlySingleLRule(rule)
  ) {
    return false;
  }
  if (rule.indexOf(',') > 0) {
    return rule
      .split(',')
      .every(
        (part) => part.indexOf('#') < 0 && isValidCronRulePart(part, partType)
      );
  }
  if (rule === '*') {
    return true;
  }
  if (
    (rule === '?' || rule === 'L') &&
    (partType === CRON_PART_TYPE.dayOfMonth ||
      partType === CRON_PART_TYPE.dayOfWeek)
  ) {
    return true;
  }
  if (!regExpRulePartValidator(rule, partType)) {
    return false;
  }
  if (
    !numberRegExp.test(rule) &&
    partType === CRON_PART_TYPE.month &&
    monthReplaces.some((monthReplace) =>
      getReplaceRegExp(monthReplace[0]).test(rule.toUpperCase())
    )
  ) {
    return true;
  }
  if (
    !numberRegExp.test(rule) &&
    partType === CRON_PART_TYPE.dayOfWeek &&
    dayWeekReplaces.some((dayWeekReplace) =>
      getReplaceRegExp(dayWeekReplace[0]).test(rule.toUpperCase())
    )
  ) {
    return true;
  }
  if (rule.endsWith('W')) {
    const rulePart = rule.toString().substring(0, rule.indexOf('W'));
    return (
      (rulePart === 'L' || numberRegExp.test(rulePart)) &&
      isValidCronRulePart(rulePart, partType)
    );
  }
  if (rule.startsWith('L')) {
    let rulePart = rule.toString().substr(1);
    if (rulePart.startsWith('-')) {
      rulePart = rulePart.substr(1);
    }
    return (
      numberRegExp.test(rulePart) && isValidCronRulePart(rulePart, partType)
    );
  }
  if (rule.endsWith('L')) {
    const rulePart = rule.toString().substring(0, rule.indexOf('L'));
    return (
      numberRegExp.test(rulePart) && isValidCronRulePart(rulePart, partType)
    );
  }
  if (partType === CRON_PART_TYPE.dayOfWeek && rule.indexOf('#') > -1) {
    const ruleParts = rule.split('#');
    if (ruleParts.length > 2) {
      return false;
    }
    if (
      isValidCronRulePart(ruleParts[0], partType) &&
      (numberRegExp.test(ruleParts[1]) || dayWeekReplaces[ruleParts[1]])
    ) {
      const numericValue = parseInt(
        numberRegExp.test(ruleParts[1])
          ? ruleParts[1]
          : dayWeekReplaces[ruleParts[1]],
        10
      );
      return numericValue >= 1 && numericValue <= 5;
    }
    return false;
  }
  try {
    if (numberRegExp.test(rule)) {
      const numericValue = parseInt(rule, 10);
      switch (partType) {
        case CRON_PART_TYPE.minutes:
          return numericValue >= 0 && numericValue <= 59;
        case CRON_PART_TYPE.hours:
          return numericValue >= 0 && numericValue <= 23;
        case CRON_PART_TYPE.dayOfMonth:
          return numericValue >= 1 && numericValue <= 31;
        case CRON_PART_TYPE.month:
          return numericValue >= 1 && numericValue <= 12;
        case CRON_PART_TYPE.dayOfWeek:
          return numericValue >= 1 && numericValue <= 7;
        case CRON_PART_TYPE.year:
          return numericValue >= minCronYear && numericValue <= maxCronYear;
        default:
          return false;
      }
    }
    if (rule.indexOf(',') > -1) {
      const ruleParts = rule.split(',');
      // for the expression type "n-th day of the week" only 1 rule is allowed (e.g. "3#2,2#2" is not a valid expression)
      if (
        partType === CRON_PART_TYPE.dayOfWeek &&
        ruleParts.some((part) => part.indexOf('#') > -1)
      ) {
        return false;
      }
      return ruleParts.every((part) => isValidCronRulePart(part, partType));
    }
    if (rule.indexOf('/') > -1) {
      const ruleParts = rule.split('/');

      if (ruleParts.length > 2) {
        return false;
      }
      return (
        isValidCronRulePart(ruleParts[0], partType) &&
        numberRegExp.test(ruleParts[1])
      );
    }
    if (rule.indexOf('-') > -1) {
      const ruleParts = rule.split('-');
      return (
        isValidCronRulePart(ruleParts[0], partType) &&
        isValidCronRulePart(ruleParts[1], partType)
      );
    }
  } catch (e) {
    return false;
  }
  return false;
}

function hasOnlyOneOfDayOfWeekAndDayOfMonthSpecified(
  dayOfMonthRule,
  dayOfWeekRule
) {
  // XOR; Day of Week or Day of Month has to be "?" but not both
  return (
    (dayOfMonthRule === '?' || dayOfWeekRule === '?') &&
    !(dayOfMonthRule === '?' && dayOfWeekRule === '?')
  );
}

let minutes;
let hours;
let daysOfMonth;
let months;
let daysOfWeek;
let years;

function nextInternal(fromDate, initialDate, counter = 0) {
  if (counter === 100) {
    return null;
  }

  const startYear = fromDate.getUTCFullYear();
  const startMonth = fromDate.getUTCMonth() + 1;
  const startDayOfMonth = fromDate.getUTCDate();
  const startHour = fromDate.getUTCHours();
  const startMinute = fromDate.getUTCMinutes();

  const year = years.find((yearsPart) => yearsPart >= startYear);
  if (!year) {
    return null;
  }

  const month = months.find(
    (monthPart) => monthPart >= (year === startYear ? startMonth : 1)
  );
  if (!month) {
    return nextInternal(
      new Date(Date.UTC(initialDate.getUTCFullYear() + 1, 0, 1, 0, 0)),
      initialDate,
      counter + 1
    );
  }

  if (daysOfWeek.length === 1 && daysOfWeek[0] === 'L') {
    daysOfWeek = [7];
  }

  const isSameMonth = year === startYear && month === startMonth;
  let possibleDaysOfMonth = daysOfMonth;
  if (possibleDaysOfMonth.length === 0) {
    possibleDaysOfMonth = getDaysOfMonthFromDaysOfWeek(year, month, daysOfWeek);
  } else if (possibleDaysOfMonth[0] === 'LW') {
    possibleDaysOfMonth = getDaysOfMonthForLW(year, month);
  } else if (possibleDaysOfMonth[0] === 'L') {
    const res = getDaysOfMonthForL(year, month);
    if (possibleDaysOfMonth.length === 2) {
      possibleDaysOfMonth = [
        res[0] + Number.parseInt(possibleDaysOfMonth[1], 10),
      ];
    } else {
      possibleDaysOfMonth = res;
    }
  } else if (possibleDaysOfMonth[0] === 'W') {
    const daysinCurrentMonth = new Date(year, month, 0).getDate();

    if (possibleDaysOfMonth[1] <= daysinCurrentMonth) {
      possibleDaysOfMonth = getDaysOfMonthForW(
        year,
        month,
        possibleDaysOfMonth[1]
      );
    } else {
      return nextInternal(
        new Date(Date.UTC(year, month, possibleDaysOfMonth[1], 0, 0)),
        initialDate,
        counter + 1
      );
    }
  }

  possibleDaysOfMonth = possibleDaysOfMonth.filter((day) =>
    isValidDate(year, month, day)
  );
  const dayOfMonth = possibleDaysOfMonth.find(
    (day) => day >= (isSameMonth ? startDayOfMonth : 1)
  );
  if (!dayOfMonth) {
    return nextInternal(
      new Date(Date.UTC(year, month, 1, 0, 0)),
      initialDate,
      counter + 1
    );
  }

  const isSameDate = isSameMonth && dayOfMonth === startDayOfMonth;

  const hour = hours.find((h) => h >= (isSameDate ? startHour : 0));
  if (hour === undefined) {
    return nextInternal(
      new Date(Date.UTC(year, month - 1, dayOfMonth + 1, 0, 0)),
      initialDate,
      counter + 1
    );
  }

  const minute = minutes.find(
    (m) => m >= (isSameDate && hour === startHour ? startMinute : 0)
  );
  if (minute === undefined) {
    return nextInternal(
      new Date(Date.UTC(year, month - 1, dayOfMonth, hour + 1, 0)),
      initialDate,
      counter + 1
    );
  }

  return new Date(Date.UTC(year, month - 1, dayOfMonth, hour, minute));
}

function getNextTriggers(fromDate = new Date(), cronExpression) {
  minutes = parseSingleRule(cronExpression.minutes, 0, 59);
  hours = parseSingleRule(cronExpression.hours, 0, 23);
  daysOfMonth = parseSingleRule(cronExpression.dayOfMonth, 1, 31);
  months = parseSingleRule(replace(cronExpression.month, monthReplaces), 1, 12);
  daysOfWeek = parseSingleRule(
    replace(cronExpression.dayOfWeek, dayWeekReplaces),
    1,
    7
  );
  years = parseSingleRule(cronExpression.year || '*', minCronYear, maxCronYear);
  const startDate = new Date(
    (Math.floor(fromDate.getTime() / 60000) + 1) * 60000
  ); // +1 min
  try {
    return nextInternal(startDate, startDate, 0);
  } catch (e) {
    return null;
  }
}
export {
  maxCronYear,
  minCronYear,
  isValidCronRulePart,
  getNextTriggers,
  hasOnlyOneOfDayOfWeekAndDayOfMonthSpecified,
  CRON_PART_TYPE,
};
